import React from 'react';
import { calc } from '@ok/blade';
import { Tooltip } from '@ok/okd';
import dayjs from 'dayjs';
import { toLocale } from '@common/i18n';
import utils from '@common/utils';
import { getById } from '@common/utils/dom';
import { PLACEHOLDER } from '@pages/currencyInfo/common/context';

import './index.less';

class PriceInfo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showChainTooltip: false,
    };
  }

  displayPrice = (price, quotePrecision, needZero) => {
    if (utils.isNumber(price)) {
      return (
        <>
          <span className="list-item-unit-symbol">¥</span>
          {calc.showFloorTruncation(price, quotePrecision, needZero)}
        </>
      );
    }
    return PLACEHOLDER;
  };

  displayCap = (marketCap) => {
    if (utils.isNumber(marketCap)) {
      return (
        <>
          <span className="list-item-unit-symbol">¥</span>
          {calc.floorShort(marketCap)}
        </>
      );
    }
    return PLACEHOLDER;
  };

  displayCurrencyMaxOffer = () => {
    const { currencyPublicInfo, currencyInfo } = this.props;
    const { distributionAmount = '' } = currencyPublicInfo;
    const { symbol } = currencyInfo;
    return utils.isNumber(distributionAmount) ? (
      <>
        <span className="list-amount-span">
          {calc.showFloorTruncation(distributionAmount)}
        </span>
        {symbol}
      </>
    ) : (
      PLACEHOLDER
    );
  };

  displayCirculationAmount = () => {
    const { currencyPublicInfo, currencyInfo } = this.props;
    const { circulationAmount = '' } = currencyPublicInfo;
    const { symbol } = currencyInfo;
    return utils.isNumber(circulationAmount) ? (
      <>
        <span className="list-amount-span">
          {calc.showFloorTruncation(circulationAmount)}
        </span>
        {symbol}
      </>
    ) : (
      PLACEHOLDER
    );
  };

  displayCirculationRate = () => {
    const { currencyPublicInfo } = this.props;
    const { circulationAmount = '', distributionAmount = '' } =
      currencyPublicInfo;
    const circulationRate = circulationAmount / distributionAmount;
    return utils.isNumber(circulationAmount) ? (
      <>
        {calc.showFloorTruncation(circulationRate * 100, 2)}
        <span className="list-item-percentage">%</span>
      </>
    ) : (
      PLACEHOLDER
    );
  };

  componentDidUpdate(prevProps) {
    if (
      this.props.currencyPublicInfo.symbol &&
      prevProps.currencyPublicInfo.symbol !==
        this.props.currencyPublicInfo.symbol
    ) {
      this.handleResize();
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const { currencyPublicInfo } = this.props;
    const { deployPublicBlockchainNames } = currencyPublicInfo;

    const chainNames = deployPublicBlockchainNames || [];
    const chainNamesStr = chainNames.join('、');
    // if no chainNamesStr, no element, will render bug, empty page
    if (chainNamesStr) {
      const parentElement = getById('list-chain-item-wrapper');
      const childElement = getById('list-chain-child-item');

      // for different extension direction, such as from 380 to 1000 and from 1000 to 380
      if (childElement.offsetWidth < parentElement.offsetWidth) {
        this.setState({
          showChainTooltip: false,
        });
      } else {
        this.setState({
          showChainTooltip: true,
        });
      }
    }
  };

  render() {
    const { showChainTooltip } = this.state;
    const { currencyInfo, currencyPublicInfo } = this.props;
    const {
      deployPublicBlockchainNames,
      highestPrice = '',
      lowestPrice,
      marketCap,
      releaseTime,
      recognitionMethod,
      quotePrecision,
      athDate,
      atlDate,
    } = currencyPublicInfo;

    const { symbol = PLACEHOLDER, fullName = PLACEHOLDER } = currencyInfo;

    const chainNames = deployPublicBlockchainNames || [];
    const chainNamesStr = chainNames.join('、');

    const dateFormatTemp = `YYYY${toLocale(
      'currency_year_split_symbol'
    )}MM${toLocale('currency_month_split_symbol')}DD${toLocale(
      'currency_day_split_symbol'
    )}`;
    const highestPriceDate = dayjs(athDate).format(dateFormatTemp);
    const lowestPriceDate = dayjs(atlDate).format(dateFormatTemp);
    const releaseTimeDate = dayjs(releaseTime).format(dateFormatTemp);

    return (
      <div className="price-info-wrapper">
        <div className="list-title">
          <div className="date-range">
            {toLocale('currency_market_info', {
              currencyFullName: fullName,
              currencyName: symbol,
            })}
          </div>
        </div>
        <div className="list-content">
          <div className="list-item">
            <div className="title">{toLocale('currency_market_value')}</div>
            <div className="value">{this.displayCap(marketCap)}</div>
          </div>
          <div className="list-item">
            {Boolean(athDate) === true ? (
              <Tooltip title={highestPriceDate} maxWidth={180}>
                <div className="title item-underline">
                  {toLocale('currency_history_highest_price')}
                </div>
              </Tooltip>
            ) : (
              <div className="title">
                {toLocale('currency_history_highest_price')}
              </div>
            )}
            <div className="value">
              {this.displayPrice(highestPrice, quotePrecision, false)}
            </div>
          </div>
          <div className="list-item">
            {Boolean(atlDate) === true ? (
              <Tooltip title={lowestPriceDate} maxWidth={180}>
                <div className="title item-underline">
                  {toLocale('currency_history_lowest_price')}
                </div>
              </Tooltip>
            ) : (
              <div className="title">
                {toLocale('currency_history_lowest_price')}
              </div>
            )}
            <div className="value">
              {this.displayPrice(lowestPrice, quotePrecision, false)}
            </div>
          </div>

          <div className="list-item">
            <div className="title"> {toLocale('currency_first_release')}</div>
            <div className="value">
              {releaseTime ? releaseTimeDate : PLACEHOLDER}
            </div>
          </div>

          <div className="list-item">
            <Tooltip title={toLocale('currency_history_max_offer_tip')}>
              <div className="title item-underline">
                {toLocale('currency_history_max_offer')}
              </div>
            </Tooltip>
            <div className="value">{this.displayCurrencyMaxOffer()}</div>
          </div>

          <div className="list-item">
            <Tooltip title={toLocale('currency_circulation_amount_tip')}>
              <div className="title item-underline">
                {toLocale('currency_circulation_amount')}
              </div>
            </Tooltip>
            <div className="value">{this.displayCirculationAmount()}</div>
          </div>

          <div className="list-item item-underline">
            <Tooltip title={toLocale('currency_circulation_rate_tip')}>
              <div className="title item-underline">
                {toLocale('currency_circulation_rate')}
              </div>
            </Tooltip>
            <div className="value">{this.displayCirculationRate()}</div>
          </div>

          {recognitionMethod && (
            <div className="list-item">
              <div className="title"> {toLocale('currency_admit_ways')}</div>
              <div className="value">{recognitionMethod}</div>
            </div>
          )}
          {chainNamesStr && (
            <div className="list-item" id="list-chain-item-wrapper">
              <div className="title">{toLocale('currency_belong_link')}</div>
              <div className="item-hidden">
                <span id="list-chain-child-item"> {chainNamesStr}</span>
              </div>
              {showChainTooltip === true ? (
                <Tooltip
                  title={chainNamesStr}
                  placement={Tooltip.DIRECTION.bottomLeft}
                  className="value-wrapper"
                >
                  <div className="value item-underline">{chainNamesStr}</div>
                </Tooltip>
              ) : (
                <div className="value">{chainNamesStr}</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default PriceInfo;
